/**
 * Error helpers for distinguishing "not found" from "service unavailable"
 * in React Query error paths. Public pages use these to show different UI:
 * a 404 gets a helpful "this isn't available" message, while a service
 * error gets a retry button.
 */

export class NotFoundError extends Error {
  readonly kind = "not_found" as const;
  constructor(message = "Not found") {
    super(message);
    this.name = "NotFoundError";
  }
}

export class ServiceError extends Error {
  readonly kind = "service_error" as const;
  readonly cause?: unknown;
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "ServiceError";
    this.cause = cause;
  }
}

/**
 * PostgREST error returned by Supabase when `.single()` matches zero rows.
 * We treat this as a 404, not a service outage.
 */
const POSTGREST_NO_ROWS = "PGRST116";

type MaybePostgrestError = {
  code?: string | null;
  message?: string | null;
  details?: string | null;
};

export function isNotFoundError(err: unknown): boolean {
  if (err instanceof NotFoundError) return true;
  const pg = err as MaybePostgrestError | null;
  return !!pg && pg.code === POSTGREST_NO_ROWS;
}

/**
 * Called from a queryFn when Supabase hands back a non-404 error. Wraps it
 * in a ServiceError so the UI can differentiate from the `!data` path. Use
 * with `.maybeSingle()` + an explicit `!data` NotFoundError check to get
 * the cleanest split.
 */
export function throwAsServiceError(err: unknown): never {
  if (isNotFoundError(err)) {
    throw new NotFoundError((err as MaybePostgrestError)?.message ?? "Not found");
  }
  const msg = (err as Error | null)?.message ?? "Service unavailable";
  throw new ServiceError(msg, err);
}

export function isServiceError(err: unknown): boolean {
  return err instanceof ServiceError;
}
